import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Building2, Download, Upload, Shield, AlertTriangle, FileDown, Wrench, UserPlus, Users, Trash2, Percent } from "lucide-react";
import { generateCompanyProfilePDF } from "@/lib/companyProfilePdf";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfing, setPdfing] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setForm(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", form);
      setForm(data);
      toast.success("Firma bilgileri kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!form?.name) { toast.error("Önce firma ünvanını doldurup kaydedin"); return; }
    setPdfing(true);
    try {
      await generateCompanyProfilePDF(form);
      toast.success("Firma kartı PDF olarak indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı: " + (e?.message || ""));
    } finally {
      setPdfing(false);
    }
  };

  if (!form) {
    return <div className="text-neutral-500 text-sm">Yükleniyor…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <p className="label-caps">Ayarlar</p>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Firma Bilgileri</h1>
        <p className="text-sm text-neutral-600 mt-2">
          Buradaki bilgiler <b>mutabakat</b>, <b>ekstre</b>, <b>çek bordrosu</b> ve <b>fatura</b> gibi tüm PDF çıktılarında
          otomatik olarak "FİRMAMIZ" bölümüne yansır.
        </p>
      </header>

      <form onSubmit={save} className="brutal-card p-6 space-y-4" data-testid="settings-company-form">
        <div className="flex items-center gap-2 pb-3 border-b border-black/10">
          <Building2 size={16} />
          <h3 className="font-semibold">Kimlik & İletişim</h3>
        </div>

        <F label="Ünvan">
          <input
            required
            data-testid="settings-company-name"
            value={form.name || ""}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input"
          />
        </F>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Vergi Dairesi">
            <input
              data-testid="settings-company-tax-office"
              value={form.tax_office || ""}
              onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
              className="input"
              placeholder="Örn: Küçükyalı VD"
            />
          </F>
          <F label="Vergi No / VKN">
            <input
              data-testid="settings-company-tax-id"
              value={form.tax_id || ""}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              className="input font-mono-num"
              placeholder="10 haneli"
            />
          </F>
        </div>

        <F label="Adres">
          <textarea
            rows={3}
            data-testid="settings-company-address"
            value={form.address || ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="input"
            placeholder="Mahalle, cadde, no, ilçe / il"
          />
        </F>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Telefon">
            <input
              data-testid="settings-company-phone"
              value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="input font-mono-num"
              placeholder="0216 000 00 00"
            />
          </F>
          <F label="E-posta">
            <input
              type="email"
              data-testid="settings-company-email"
              value={form.email || ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input"
              placeholder="muhasebe@plasper.com.tr"
            />
          </F>
        </div>

        <F label="Web Sitesi">
          <input
            data-testid="settings-company-website"
            value={form.website || ""}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="input"
            placeholder="www.plasper.com.tr"
          />
        </F>

        <div className="pt-3 border-t border-black/10">
          <h3 className="font-semibold mb-3">Banka Bilgileri</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Banka">
              <input
                data-testid="settings-company-bank"
                value={form.bank || ""}
                onChange={(e) => setForm({ ...form, bank: e.target.value })}
                className="input"
                placeholder="Örn: Garanti BBVA"
              />
            </F>
            <div className="md:col-span-2">
              <F label="IBAN">
                <input
                  data-testid="settings-company-iban"
                  value={form.iban || ""}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  className="input font-mono-num"
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                />
              </F>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-black/10 gap-2">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={pdfing || !form?.name}
            data-testid="settings-company-pdf"
            className="brutal-btn bg-white text-black border border-black px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-40"
            title="Firma bilgilerini müşterilerle paylaşılabilir tek sayfa PDF olarak indir"
          >
            <FileDown size={14} />
            {pdfing ? "Hazırlanıyor…" : "PDF Olarak İndir"}
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-company-save"
            className="brutal-btn bg-black text-white px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Save size={14} />
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </form>

      <PricingUsersSection />
      <BackupSection />
      <MaintenanceSection />

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.55rem 0.75rem;outline:none;font-size:0.9rem}
                .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
    </div>
  );
}

function MaintenanceSection() {
  const [running, setRunning] = useState(null); // null | "quotes" | "kg"
  const [result, setResult] = useState(null);
  const [kgResult, setKgResult] = useState(null);

  const runBackfill = async () => {
    if (!window.confirm("Eski tek kalemli fiyat teklifleri onarılsın mı? Bu işlem güvenli ve tekrar çalıştırılabilir.")) return;
    setRunning("quotes");
    setResult(null);
    try {
      const { data } = await api.post("/admin/migrate/backfill-quote-items");
      setResult(data);
      toast.success(`${data.wrapped ?? data.fixed ?? 0} teklif sarıldı · ${data.cleaned ?? 0} temizlendi`);
    } catch (e) {
      toast.error("Onarım başarısız: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setRunning(null);
    }
  };

  const runKgFix = async () => {
    if (!window.confirm("Karton gramajı kg cinsinden girilmiş kayıtlar (0 < değer < 1) 1000 ile çarpılarak g/m²'ye çevrilsin mi?")) return;
    setRunning("kg");
    setKgResult(null);
    try {
      const { data } = await api.post("/admin/migrate/fix-kg-gsm");
      setKgResult(data);
      toast.success(`Fiyat listesi: ${data.price_list_items_fixed} · Stok: ${data.stock_items_fixed} onarıldı`);
    } catch (e) {
      toast.error("Onarım başarısız: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="brutal-card p-6 space-y-4 mt-6" data-testid="settings-maintenance-section">
      <div className="flex items-center gap-2 pb-3 border-b border-black/10">
        <Wrench size={16} />
        <h3 className="font-semibold">Bakım & Onarım</h3>
      </div>

      <div className="border-2 border-black/20 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h4 className="font-semibold text-sm">Fiyat Teklifleri — Kalem Yapısı Onarımı</h4>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
              26 Eylül öncesi tek kalemli (sepet kullanmadan) kaydedilmiş tekliflerde ürün detayı{" "}
              <code className="bg-neutral-100 px-1 border border-black/10">items[]</code> olarak
              yazılmamıştı. Bu yüzden Tedarikçi Sipariş dialog'unda Birim/Toplam boş kalıyordu.
              Bu buton eski teklifleri yeni yapıya taşır. <b>Idempotent</b> — tekrar çalıştırmak zararsız.
            </p>
            <p className="text-[11px] text-neutral-500 mt-1">
              Not: Eski kayıtlarda tedarikçi maliyet snapshot'ı bulunmadığından sipariş dialog'unda Birim'i düzenleyebilirsin.
            </p>
          </div>
          <button
            onClick={runBackfill}
            disabled={running === "quotes"}
            data-testid="settings-backfill-quotes"
            className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Wrench size={14} />
            {running === "quotes" ? "Onarılıyor…" : "Şimdi Onar"}
          </button>
        </div>
        {result && (
          <div className="text-xs bg-white border border-black/10 p-3 grid grid-cols-3 gap-2 font-mono-num"
               data-testid="settings-backfill-result">
            <div>
              <div className="label-caps">Toplam</div>
              <div className="text-lg font-semibold">{result.total_quotes}</div>
            </div>
            <div>
              <div className="label-caps">Aday</div>
              <div className="text-lg font-semibold">{result.candidates}</div>
            </div>
            <div>
              <div className="label-caps text-emerald-700">Onarılan</div>
              <div className="text-lg font-semibold text-emerald-700">+{result.fixed}</div>
            </div>
          </div>
        )}
      </div>

      <div className="border-2 border-black/20 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h4 className="font-semibold text-sm">Karton Gramajı — kg → g/m² Onarımı</h4>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
              Fiyat listesi kalemlerinde ve stok kartlarında yanlışlıkla{" "}
              <b>kg cinsinden</b> girilmiş gramaj değerlerini (0 &lt; değer &lt; 1) düzeltir.
              Değeri <b>1000 ile çarpar</b> — örn.{" "}
              <code className="bg-neutral-100 px-1 border border-black/10">0,135 kg</code> →{" "}
              <code className="bg-neutral-100 px-1 border border-black/10">135 g/m²</code>. Idempotent.
            </p>
          </div>
          <button
            onClick={runKgFix}
            disabled={running === "kg"}
            data-testid="settings-fix-kg-gsm"
            className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Wrench size={14} />
            {running === "kg" ? "Onarılıyor…" : "Şimdi Onar"}
          </button>
        </div>
        {kgResult && (
          <div className="text-xs bg-white border border-black/10 p-3 grid grid-cols-2 gap-2 font-mono-num"
               data-testid="settings-fix-kg-result">
            <div>
              <div className="label-caps">Fiyat Listesi Kalemi</div>
              <div className="text-lg font-semibold text-emerald-700">+{kgResult.price_list_items_fixed}</div>
            </div>
            <div>
              <div className="label-caps">Stok Kartı</div>
              <div className="text-lg font-semibold text-emerald-700">+{kgResult.stock_items_fixed}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BackupSection() {
  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileRef = useRef(null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plasper-finans-yedek-${ts}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("JSON yedeği indirildi");
    } catch (e) {
      toast.error("Yedek alınamadı: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await api.get("/backup/export-excel", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plasper-finans-yedek-${ts}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Excel yedeği indirildi");
    } catch (e) {
      toast.error("Excel yedek alınamadı: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleFilePick = (file) => {
    setSelectedFile(file || null);
    setRestoreResult(null);
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      toast.error("Önce bir yedek dosyası seç");
      return;
    }
    if (restoreConfirm.trim() !== "YEDEKTEN GERI YUKLE") {
      toast.error('Onaylamak için "YEDEKTEN GERI YUKLE" yaz');
      return;
    }
    setRestoring(true);
    try {
      const text = await selectedFile.text();
      const parsed = JSON.parse(text);
      if (parsed?.app !== "plasper-finans") {
        toast.error("Geçersiz yedek dosyası");
        setRestoring(false);
        return;
      }
      const { data } = await api.post("/backup/restore", {
        confirm: "YEDEKTEN GERI YUKLE",
        backup: parsed,
      });
      setRestoreResult(data);
      setRestoreConfirm("");
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Yedekten geri yüklendi");
    } catch (e) {
      toast.error("Geri yükleme hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="brutal-card p-6 space-y-5 mt-6" data-testid="settings-backup-section">
      <div className="flex items-center gap-2 pb-3 border-b border-black/10">
        <Shield size={16} />
        <h3 className="font-semibold">Yedekleme & Geri Yükleme</h3>
      </div>

      <p className="text-sm text-neutral-600">
        Bu bölümden tüm iş verilerinin (cariler, faturalar, işlemler, çekler vb.) tam bir kopyasını{" "}
        <b>JSON dosyası</b> olarak bilgisayarına indirebilir, gerektiğinde geri yükleyebilirsin.
        Verilerini <b>haftada en az bir kez</b> yedeklemeni öneririz.
      </p>

      {/* Download */}
      <div className="border-2 border-black p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Download size={16} />
          <h4 className="font-semibold">Yedek İndir</h4>
        </div>
        <p className="text-xs text-neutral-500">
          Aynı verinin iki farklı formatı: <b>JSON</b> geri yükleme için, <b>Excel</b> insan-okur arşiv için.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="settings-backup-download"
            className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={14} />
            {downloading ? "Hazırlanıyor…" : "JSON Yedek İndir"}
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
            data-testid="settings-backup-download-excel"
            className="brutal-btn bg-emerald-600 text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={14} />
            {downloadingExcel ? "Hazırlanıyor…" : "Excel Yedek İndir"}
          </button>
        </div>
        <div className="text-[11px] text-neutral-500 pt-1 border-t border-black/5">
          💡 <b>JSON</b>: Sadece geri yüklemek istersen kullan. <b>Excel</b>: Arşiv ve raporlama için ideal; her koleksiyon ayrı sayfa.
        </div>
      </div>

      {/* Restore */}
      <div className="border-2 border-rose-500 p-4 space-y-3 bg-rose-50/40">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-rose-600" />
          <h4 className="font-semibold text-rose-700">Yedekten Geri Yükle (Yıkıcı İşlem)</h4>
        </div>
        <p className="text-xs text-rose-800">
          <b>DİKKAT</b>: Geri yükleme mevcut tüm verini <b>silip</b> yedekteki verilerle değiştirir. Bu işlem geri alınamaz.
          Sadece bilinçli bir kayıp durumunda kullan.
        </p>

        <div className="space-y-2">
          <label className="label-caps text-xs">1. Yedek Dosyasını Seç</label>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={(e) => handleFilePick(e.target.files?.[0])}
            data-testid="settings-backup-file"
            className="text-xs"
          />
          {selectedFile && (
            <p className="text-xs text-neutral-600">Seçili: <b>{selectedFile.name}</b> ({Math.round(selectedFile.size / 1024)} KB)</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="label-caps text-xs">2. Onay için aşağıya <b>YEDEKTEN GERI YUKLE</b> yaz</label>
          <input
            type="text"
            value={restoreConfirm}
            onChange={(e) => setRestoreConfirm(e.target.value)}
            placeholder="YEDEKTEN GERI YUKLE"
            data-testid="settings-backup-confirm"
            className="input font-mono-num"
          />
        </div>

        <button
          onClick={handleRestore}
          disabled={restoring || !selectedFile || restoreConfirm.trim() !== "YEDEKTEN GERI YUKLE"}
          data-testid="settings-backup-restore"
          className="brutal-btn bg-rose-600 text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
        >
          <Upload size={14} />
          {restoring ? "Geri yükleniyor…" : "Geri Yükle"}
        </button>

        {restoreResult && (
          <div className="mt-3 p-3 bg-white border border-black/10 text-xs" data-testid="settings-backup-result">
            <div className="font-semibold text-emerald-700 mb-2">Geri yükleme tamamlandı</div>
            <div className="text-neutral-500 text-[11px] mb-2">
              Yedek tarihi: {restoreResult.restored_from}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {Object.entries(restoreResult.summary || {}).map(([coll, s]) => (
                <div key={coll} className="flex justify-between">
                  <span className="text-neutral-600">{coll}</span>
                  <span className="font-mono-num">
                    <span className="text-rose-500">−{s.deleted}</span>
                    {" "}
                    <span className="text-emerald-600">+{s.restored}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}


function PricingUsersSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", cost_markup_percent: "" });
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/pricing-users");
      setRows(data || []);
    } catch (e) {
      toast.error("Yetkili kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addRow = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) { toast.error("Geçerli bir e-posta girin"); return; }
    setAdding(true);
    try {
      await api.post("/pricing-users", {
        email: form.email.trim().toLowerCase(),
        cost_markup_percent: Number(form.cost_markup_percent || 0),
        is_active: true,
      });
      setForm({ email: "", cost_markup_percent: "" });
      await load();
      toast.success("Yetki eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally {
      setAdding(false);
    }
  };

  const updateRow = async (row, patch) => {
    setSavingId(row.id);
    try {
      await api.patch(`/pricing-users/${row.id}`, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`${row.email} adresinin yetkisi kaldırılsın mı? Bu kullanıcı bir daha giriş yapamaz.`)) return;
    setSavingId(row.id);
    try {
      await api.delete(`/pricing-users/${row.id}`);
      await load();
      toast.success("Yetki kaldırıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="brutal-card p-6 space-y-5 mt-6" data-testid="settings-pricing-users">
      <div className="flex items-center gap-2 pb-3 border-b border-black/10">
        <Users size={16} />
        <h3 className="font-semibold">Fiyat Hesaplama Yetkileri</h3>
      </div>

      <p className="text-sm text-neutral-600 leading-relaxed">
        Aşağıya eklediğiniz e-posta sahipleri Google ile giriş yaptığında yalnızca{" "}
        <b>Fiyat Hesaplama</b> modülünü görür. Stok, teklif listeleri, cari hesap ve diğer
        modüllere erişemez, hiçbir teklif kaydı oluşturamaz — sadece <b>hesaplama + PDF çıktısı</b> alabilir.
        Her kullanıcıya özel bir <b>Maliyet Artış %</b> tanımlarsınız: karton m² maliyeti, sizin gördüğünüzden
        bu oran kadar <b>şişirilmiş</b> şekilde onlara gösterilir. Kâr marjını bu maliyet üzerinden hesaplarlar,
        sizin gerçek maliyetinizi asla göremezler.
      </p>

      <form onSubmit={addRow} className="border-2 border-black p-4 space-y-3" data-testid="pricing-user-add-form">
        <div className="flex items-center gap-2">
          <UserPlus size={14} />
          <h4 className="font-semibold text-sm">Yeni Yetkili Kullanıcı</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_auto] gap-2">
          <input
            type="email"
            required
            data-testid="pricing-user-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="ornek@firma.com"
            className="input"
          />
          <div className="relative">
            <input
              type="number"
              step="0.1"
              min="0"
              max="500"
              required
              data-testid="pricing-user-markup"
              value={form.cost_markup_percent}
              onChange={(e) => setForm({ ...form, cost_markup_percent: e.target.value })}
              placeholder="Maliyet %"
              className="input font-mono-num pr-8"
            />
            <Percent size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          </div>
          <button
            type="submit"
            disabled={adding}
            data-testid="pricing-user-add-btn"
            className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40 whitespace-nowrap"
          >
            <UserPlus size={13} />
            {adding ? "Ekleniyor…" : "Ekle"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500">
          💡 Örnek: %15 → 100 ₺/m² olan kartonu bu kullanıcı <b>115 ₺/m²</b> olarak görür.
        </p>
      </form>

      {loading ? (
        <div className="text-sm text-neutral-500">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-neutral-500 italic text-center py-6 border border-dashed border-black/10">
          Henüz yetkili kullanıcı eklenmemiş.
        </div>
      ) : (
        <table className="w-full text-sm border border-black/10" data-testid="pricing-users-table">
          <thead className="bg-neutral-100 border-b border-black/10">
            <tr>
              <th className="text-left px-3 py-2 font-normal text-neutral-500 uppercase tracking-widest text-[11px]">E-posta</th>
              <th className="text-right px-3 py-2 font-normal text-neutral-500 uppercase tracking-widest text-[11px]">Maliyet %</th>
              <th className="text-center px-3 py-2 font-normal text-neutral-500 uppercase tracking-widest text-[11px]" title="100 ₺ liste fiyatı bu kullanıcıya ne görünür?">100 ₺ → Kullanıcı</th>
              <th className="text-center px-3 py-2 font-normal text-neutral-500 uppercase tracking-widest text-[11px]">Durum</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const markup = Number(r.cost_markup_percent || 0);
              const preview = 100 * (1 + markup / 100);
              return (
              <tr key={r.id} className="border-b border-black/5" data-testid={`pricing-user-row-${r.email}`}>
                <td className="px-3 py-2 font-mono-num">{r.email}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="500"
                    defaultValue={r.cost_markup_percent}
                    disabled={savingId === r.id}
                    data-testid={`pricing-user-markup-${r.email}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== r.cost_markup_percent) updateRow(r, { cost_markup_percent: v });
                    }}
                    className="w-24 border border-black/20 px-2 py-1 text-right font-mono-num text-sm outline-none focus:border-black"
                  />
                  <span className="ml-1 text-neutral-400">%</span>
                </td>
                <td className="px-3 py-2 text-center font-mono-num text-xs" data-testid={`pricing-user-preview-${r.email}`}>
                  <span className="text-neutral-400">100 ₺</span>
                  <span className="mx-1 text-neutral-300">→</span>
                  <span className="font-semibold text-emerald-700">
                    {preview.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!r.is_active}
                      disabled={savingId === r.id}
                      data-testid={`pricing-user-active-${r.email}`}
                      onChange={(e) => updateRow(r, { is_active: e.target.checked })}
                      className="w-4 h-4 accent-black"
                    />
                    <span className={`text-xs font-medium ${r.is_active ? "text-emerald-700" : "text-neutral-400"}`}>
                      {r.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => deleteRow(r)}
                    disabled={savingId === r.id}
                    data-testid={`pricing-user-delete-${r.email}`}
                    className="text-rose-600 hover:bg-rose-50 p-1.5 border border-transparent hover:border-rose-200"
                    title="Yetkiyi kaldır"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
